import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, Validators } from '@angular/forms';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvDynamicFormUtils, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { ReportFilterFormService } from './services/report-filter-form.service';
import { RoleDto } from '@pages/settings-page/access-control-settings/roles/grid/interfaces';
import DynamicFormUtils from '@shared/utils/dynamic-form.utils';
import {
  debounceTime,
  filter,
  map,
  Observable,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { ReportFrequency } from './constants';
import {
  ReportInformationTabDto,
  ReportInformationTabForm,
  Role,
  User,
} from './interfaces';
import { RecurrenceInfoService } from './services/recurrence-info-service';
import { ReportDataSaveService } from './services/report-data-save.service';
import { ReportInformationTabService } from './services/report-information-tab.service';
import { SendNowTriggerService } from './services/send-now-trigger.service';
import { DepartmentDto } from '@shared/interfaces/department';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import { ActivatedRoute } from '@angular/router';
@Component({
  selector: 'app-report-information-tab',
  templateUrl: './report-information-tab.component.html',
  styleUrls: ['report-information-tab.component.scss'],
  providers: [
    ReportInformationTabService,
    RecurrenceInfoService,
    ReportDataSaveService,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReportInformationTabComponent
  extends TabBase
  implements OnInit, PendingChanges, OnDestroy
{
  private reportFilterFormService = inject(ReportFilterFormService);
  private api = inject(RestApiService);
  private tabService = inject(ReportInformationTabService);
  private recurrenceInfoService = inject(RecurrenceInfoService);
  private dataSaveService = inject(ReportDataSaveService);
  private entityHelperService = inject(SplitterWithGridHelperService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private translate = inject(TranslateService);
  private sendNowTriggerService = inject(SendNowTriggerService);
  private cdRef = inject(ChangeDetectorRef);

  formGroup: FormGroup<FormOf<ReportInformationTabForm>>;
  mainElements: DvDynamicFormElement[];
  filterElements: DvDynamicFormElement[];
  receiverElements: DvDynamicFormElement[];
  recurrenceElements: DvDynamicFormElement[];
  frequencyElements: DvDynamicFormElement[] = [];
  lastSentElements: DvDynamicFormElement[];
  allElements: DvDynamicFormElement[] = [];

  recurrenceText: string;
  reportFrequency: typeof ReportFrequency = ReportFrequency;
  isWeeklyRecurrenceValid: boolean = true;

  private freqSubscription: Subscription | undefined;
  private canEdit: boolean;
  private destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.mainElements = this.tabService.getMainElements();

    //making sure filter elements have newest translations
    this.reportFilterFormService.filterElements =
      this.reportFilterFormService.getElements();

    this.filterElements = this.reportFilterFormService.filterElements;
    this.receiverElements = this.tabService.getReceiverElements();
    this.recurrenceElements = this.tabService.getRecurrenceElements();
    this.lastSentElements = this.tabService.getLastSentElements();
    this.allElements = [
      ...this.mainElements,
      ...this.filterElements,
      ...this.receiverElements,
      ...this.recurrenceElements,
      ...this.lastSentElements,
      ...this.tabService.getMonthFrequencyElements(),
      ...this.tabService.getWeekFrequencyElements(),
      ...this.tabService.getYearFrequencyElements(),
    ];

    this.formGroup = DvFormUtils.createFormGroup(this.allElements, true);
    this.handleAccessRestrictions();
    this.subscribeToSelectedEntityChanges();
    this.listenToRecurrenceFormChanges();
    this.listenToDataFilterChanges();
    this.listenToSendNowClicked();
  }

  ngOnDestroy(): void {
    this.freqSubscription?.unsubscribe();
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  onSave(): void {
    this.performSave().subscribe();
  }

  private performSave(): Observable<boolean> {
    const entityId =
      this.entityId$.value === NEW_ENTITY_ROUTE_PARAM
        ? '0'
        : this.entityId$.value;
    const payload = this.dataSaveService.getReportSettingUpsertPayload(
      this.formGroup.getRawValue(),
      this.receiverElements
    );

    return this.api.upsert(Entity.ReportSetting, entityId, payload).pipe(
      tap((id: number | undefined) => {
        if (id) {
          this.entityHelperService.addNewlyCreatedItemToGrid(
            Entity.ReportSetting,
            id
          );
        } else {
          this.entityHelperService.refreshRowData(entityId);
        }
        this.formGroup.markAsPristine();
      }),
      map(() => true)
    );
  }

  private subscribeToSelectedEntityChanges(): void {
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(id => !!id)
      )
      .subscribe(id => {
        if (id === NEW_ENTITY_ROUTE_PARAM) {
          this.formGroup.reset();
          this.frequencyElements = [];
          this.recurrenceText = '';
          this.listenToFrequencyChanges();
          return;
        }
        this.getAndSetExistingEntityData(id);
      });
  }

  private getAndSetExistingEntityData(id: string): void {
    const keys = DvDynamicFormUtils.getKeysForRequest(this.allElements).filter(
      field => !this.tabService.excludedRequestValues.includes(field)
    );

    this.api
      .getInfo<ReportInformationTabDto>(Entity.ReportSetting, id, keys)
      .subscribe(res => {
        this.freqSubscription?.unsubscribe();

        res.ReportType = this.translate.instant(
          `ReportType.${res.ReportType || ''}`
        );
        const formData = {
          ...res,
          ...this.recurrenceInfoService.getRecurrenceArraysValues(
            res.Frequency,
            res.ByMonth?.[0],
            res.ByDay,
            res.ByMonthDay?.[0],
            res.Offset
          ),
        } as unknown as ReportInformationTabForm;

        this.reportFilterFormService.selectedDataFilterID = res.DataFilterID;
        this.reportFilterFormService.selectedReportEntityId =
          this.entityId$.getValue();
        DvDynamicFormUtils.setFirstData(
          this.allElements,
          this.formGroup,
          formData
        );

        this.formGroup.markAsPristine();

        if (formData.Frequency) {
          this.setReoccuranceFormByFrequency(
            formData.Frequency,
            this.formGroup.controls
          );
        }
        this.listenToFrequencyChanges();

        this.setMultiselectDropdownsValues(
          res.Users,
          res.Departments,
          res.Roles
        );
      });
  }

  private setMultiselectDropdownsValues(
    users: User[],
    departments: DepartmentDto[],
    roles: Role[]
  ): void {
    const selectedUsers = users.map(user => user.UserID);
    const selectedDepartments = departments.map(
      department => department.DepartmentID
    );
    const selectedRoles = roles.map(role => role.RoleID);
    const formControls = this.formGroup.controls;

    DynamicFormUtils.setDropdownElementItems(
      this.allElements,
      'Departments',
      departments
    );
    formControls.Departments.setValue(selectedDepartments, {
      emitEvent: false,
    });

    DynamicFormUtils.setDropdownElementItems(this.allElements, 'Users', users);
    formControls.Users.setValue(selectedUsers, {
      emitEvent: false,
    });

    DynamicFormUtils.setTranslatedDropdownElementItems<RoleDto>(
      this.allElements,
      'Roles',
      roles,
      this.translate,
      'TranslationKey',
      undefined,
      'Name'
    );
    formControls.Roles.setValue(selectedRoles, {
      emitEvent: false,
    });
  }

  private listenToFrequencyChanges(): void {
    this.freqSubscription = this.formGroup.controls.Frequency?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(frequency => {
        if (frequency) {
          if (this.formGroup.controls.Frequency?.dirty) {
            this.recurrenceInfoService.resetRecurrenceControls(
              this.formGroup.controls
            );
          }
          this.setReoccuranceFormByFrequency(
            frequency,
            this.formGroup.controls
          );
        }
      });
  }

  private setReoccuranceFormByFrequency(
    frequency: ReportFrequency,
    controls: FormOf<ReportInformationTabForm>
  ): void {
    switch (frequency) {
      case ReportFrequency.Daily: {
        this.frequencyElements = [];
        this.cdRef.detectChanges();
        this.recurrenceInfoService.toggleFormElements(
          [
            ...this.tabService.getMonthFrequencyElements(),
            ...this.tabService.getYearFrequencyElements(),
            ...this.tabService.getWeekFrequencyElements(),
          ],
          this.formGroup.controls
        );
        controls.Interval.setValidators([
          Validators.min(1),
          Validators.required,
        ]);
        break;
      }
      case ReportFrequency.Weekly: {
        this.frequencyElements = this.tabService.getWeekFrequencyElements();
        this.cdRef.detectChanges();
        this.recurrenceInfoService.toggleFormElements(
          [
            ...this.tabService.getMonthFrequencyElements(),
            ...this.tabService.getYearFrequencyElements(),
          ],
          this.formGroup.controls
        );
        this.recurrenceInfoService.toggleFormElements(
          this.frequencyElements,
          this.formGroup.controls,
          false
        );
        this.recurrenceInfoService.setWeeklyRecurrenceControlValues(controls);
        controls.Interval.setValidators([
          Validators.min(1),
          Validators.max(4),
          Validators.required,
        ]);
        break;
      }
      case ReportFrequency.Monthly: {
        this.frequencyElements = this.tabService.getMonthFrequencyElements();
        this.cdRef.detectChanges();
        this.recurrenceInfoService.toggleFormElements(
          [
            ...this.tabService.getWeekFrequencyElements(),
            ...this.tabService.getYearFrequencyElements(),
          ],
          this.formGroup.controls
        );
        this.recurrenceInfoService.setMonthlyRecurrenceControlValues(controls);
        controls.Interval.setValidators([
          Validators.min(1),
          Validators.required,
        ]);
        break;
      }
      case ReportFrequency.Yearly: {
        this.frequencyElements = this.tabService.getYearFrequencyElements();
        this.cdRef.detectChanges();
        this.recurrenceInfoService.toggleFormElements(
          [
            ...this.tabService.getWeekFrequencyElements(),
            ...this.tabService.getMonthFrequencyElements(),
          ],
          this.formGroup.controls
        );
        this.recurrenceInfoService.setYearlyRecurrenceControlValues(controls);
        controls.Interval.setValidators([
          Validators.min(1),
          Validators.required,
        ]);
        break;
      }
      default: {
        this.frequencyElements = [];
        break;
      }
    }
  }

  private listenToRecurrenceFormChanges(): void {
    const { controls } = this.formGroup;
    controls.onThe?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (!this.canEdit) {
          this.formGroup.disable({ emitEvent: false });
          return;
        }
        if (val) {
          if (controls.onThe?.dirty) {
            this.recurrenceInfoService.resetRecurrenceControls(
              this.formGroup.controls
            );
          }
          controls.onDay?.reset();
          controls.on?.reset();
          if (
            this.formGroup.controls.Frequency?.value === ReportFrequency.Yearly
          ) {
            controls.ByMonthWithDay?.disable();
          }
          controls.ByMonthDay?.disable();
        } else {
          if (
            this.formGroup.controls.Frequency?.value === ReportFrequency.Yearly
          ) {
            controls.ByMonthWithDay?.enable();
          }
          controls.ByMonthDay?.enable();
        }
      });

    controls.onDay?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (!this.canEdit) {
          this.formGroup.disable({ emitEvent: false });
          return;
        }
        if (val) {
          if (controls.onDay?.dirty) {
            this.recurrenceInfoService.resetRecurrenceControls(
              this.formGroup.controls
            );
          }
          controls.onThe?.reset();
          controls.on?.reset();
          controls.Offset?.disable();
          controls.ByDay?.disable();
        } else {
          controls.Offset?.enable();
          controls.ByDay?.enable();
        }
      });

    controls.on?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (!this.canEdit) {
          this.formGroup.disable({ emitEvent: false });
          return;
        }
        if (val) {
          if (controls.on?.dirty) {
            this.recurrenceInfoService.resetRecurrenceControls(
              this.formGroup.controls
            );
          }
          controls.onThe?.reset();
          controls.onDay?.reset();
          controls.Offset?.disable();
          controls.ByDay?.disable();
          controls.ByMonthWithOffset?.disable();
        } else {
          controls.Offset?.enable();
          controls.ByDay?.enable();
          if (
            this.formGroup.controls.Frequency?.value === ReportFrequency.Yearly
          ) {
            controls.ByMonthWithOffset?.enable();
          }
        }
      });

    controls.RecurrenceActive.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOn => {
        if (isOn) {
          controls.RecurrenceStartTime?.setValue(new Date());
          controls.RecurrenceStartTime?.markAsDirty();
        }
      });

    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(value => {
        if (value.Frequency === ReportFrequency.Weekly) {
          this.isWeeklyRecurrenceValid =
            !!this.recurrenceInfoService.getSelectedWeekdays(
              value as ReportInformationTabForm
            ).length;
        } else {
          this.isWeeklyRecurrenceValid = true;
        }
        this.recurrenceText = this.recurrenceInfoService.setRecurrenceText(
          this.formGroup.controls
        );
      });
  }

  private handleAccessRestrictions(): void {
    this.permissionsService
      .hasPermissions([RolesAndPermissions.ReportEdit])
      .pipe(take(1))
      .subscribe(hasPermissions => {
        this.canEdit = hasPermissions;
        if (!hasPermissions) {
          this.formGroup.disable();
        }
      });
  }

  private listenToSendNowClicked(): void {
    this.sendNowTriggerService.sendReportNowClicked$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => this.getAndSetExistingEntityData(id));
  }

  private listenToDataFilterChanges(): void {
    this.formGroup.controls.DataFilterID.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.reportFilterFormService.toggleFilterEditButton(val);
        this.reportFilterFormService.selectedDataFilterID = val;
      });
  }
}
